import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Check, Eye, EyeOff, ArrowUpCircle, Clock } from 'lucide-react';

const AdminEventTypes = () => {
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEventTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/custom-event-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventTypes(res.data.event_types || []);
    } catch {
      toast.error('Failed to load event types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEventTypes(); }, []);

  const handleAction = async (action, name) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/event-types/${action}`, { name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Event type "${name}" ${action === 'promote' ? 'promoted to official list' : action === 'hide' ? 'hidden' : 'updated'}`);
      fetchEventTypes();
    } catch {
      toast.error('Action failed');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statusColor = {
    custom: 'bg-amber-100 text-amber-700',
    promoted: 'bg-emerald-100 text-emerald-700',
    hidden: 'bg-gray-100 text-gray-500',
    mapped: 'bg-blue-100 text-blue-700',
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  const customTypes = eventTypes.filter(t => t.status === 'custom');
  const promotedTypes = eventTypes.filter(t => t.status === 'promoted');
  const hiddenTypes = eventTypes.filter(t => t.status === 'hidden');

  return (
    <div className="space-y-6" data-testid="admin-event-types">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{customTypes.length}</p>
            <p className="text-xs text-gray-500">Pending Review</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{promotedTypes.length}</p>
            <p className="text-xs text-gray-500">Promoted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-gray-400">{hiddenTypes.length}</p>
            <p className="text-xs text-gray-500">Hidden</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending custom types */}
      {customTypes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Pending Review</h3>
          <div className="space-y-2">
            {customTypes.map(t => (
              <Card key={t.name} data-testid={`custom-type-${t.name}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{t.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{t.use_count || 1} use{(t.use_count || 1) !== 1 ? 's' : ''}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(t.last_used)}
                        </span>
                      </div>
                    </div>
                    <Badge className={statusColor[t.status]}>{t.status}</Badge>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      onClick={() => handleAction('promote', t.name)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-xs h-8"
                      data-testid={`promote-${t.name}`}
                    >
                      <ArrowUpCircle className="w-3 h-3 mr-1" />
                      Add to Official
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAction('hide', t.name)}
                      className="text-xs h-8"
                      data-testid={`hide-${t.name}`}
                    >
                      <EyeOff className="w-3 h-3 mr-1" />
                      Hide
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Promoted types */}
      {promotedTypes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Promoted to Official</h3>
          <div className="flex flex-wrap gap-2">
            {promotedTypes.map(t => (
              <Badge key={t.name} className="bg-emerald-100 text-emerald-700 px-3 py-1">
                <Check className="w-3 h-3 mr-1" />{t.name} ({t.use_count || 1})
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Hidden types */}
      {hiddenTypes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Hidden</h3>
          <div className="flex flex-wrap gap-2">
            {hiddenTypes.map(t => (
              <Badge key={t.name} variant="outline" className="text-gray-400 px-3 py-1">
                {t.name} ({t.use_count || 1})
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {eventTypes.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No custom event types submitted yet</p>
            <p className="text-xs text-gray-400 mt-1">They'll appear here when customers use the "Not listed" option</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminEventTypes;
